/**
 * Normalize mailing addresses across profile, saved addresses, and wizard state.
 * Resolves ZIP/postal code from all common field names and embedded strings.
 */

const POSTAL_KEYS = [
  'zip',
  'zip_code',
  'zipcode',
  'postal_code',
  'postalCode',
  'address_zip',
];

const NESTED_POSTAL_SOURCES = [
  'mailing_address',
  'correct_address',
  'structured_address',
  'normalized_address',
  'address',
  'consumer_info',
  'profile',
];

const STRING_ADDRESS_FIELDS = [
  'standardized_address',
  'formatted_address',
  'raw_user_input',
  'full_address',
  'current_address',
  'address',
  'correct_value',
  'anchor_value',
];

/** Extract a US ZIP (5 or 5+4) from any address-shaped object or string. */
export function extractPostalCode(source) {
  if (source === undefined || source === null) return '';

  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return '';
    const endMatch = trimmed.match(/(\d{5}(?:-\d{4})?)\s*$/);
    if (endMatch) return endMatch[1];
    const anyMatch = trimmed.match(/\b(\d{5}(?:-\d{4})?)\b/);
    return anyMatch ? anyMatch[1] : '';
  }

  if (typeof source !== 'object') {
    const asString = String(source).trim();
    return asString ? extractPostalCode(asString) : '';
  }

  for (const key of POSTAL_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }

  for (const nestedKey of NESTED_POSTAL_SOURCES) {
    const nested = source[nestedKey];
    if (nested) {
      const fromNested = extractPostalCode(nested);
      if (fromNested) return fromNested;
    }
  }

  for (const field of STRING_ADDRESS_FIELDS) {
    const text = source[field];
    if (typeof text === 'string' && text.trim()) {
      const fromText = extractPostalCode(text);
      if (fromText) return fromText;
    }
  }

  return '';
}

/** Resolved postal code on a sender/card record (zip or postal_code aliases). */
export function getResolvedPostalCode(addr) {
  return extractPostalCode(addr);
}

export function hasValidPostalCode(addr) {
  const postal = getResolvedPostalCode(addr);
  return /^\d{5}(-\d{4})?$/.test(postal);
}

/** Map backend correct_address or profile payload to a flat address record. */
export function mapCorrectAddressToProfile(ca, fallbackName = '') {
  if (!ca || typeof ca !== 'object') return null;

  const postal =
    extractPostalCode(ca) ||
    extractPostalCode(ca.standardized_address) ||
    extractPostalCode(ca.formatted_address) ||
    extractPostalCode(ca.raw_user_input);

  return {
    full_name: fallbackName,
    street_address: ca.street_line_1 || ca.standardized_address || '',
    address_line_1: ca.street_line_1 || '',
    address_line_2: ca.street_line_2 || '',
    city: ca.city || '',
    state: ca.state || '',
    zip: postal,
    postal_code: postal,
    source: 'correct_address',
  };
}

/** Map /users/profile mailing_address to flat address record. */
export function mapUserProfileToAddress(profileRes, fallbackName = '') {
  if (!profileRes) return null;

  const mailing = profileRes.mailing_address;
  const topLevel = {
    ...profileRes,
    mailing_address: mailing,
  };

  const postal = extractPostalCode(topLevel) || extractPostalCode(mailing || {});

  const fullName =
    profileRes.full_name ||
    profileRes.name ||
    `${profileRes.first_name || ''} ${profileRes.last_name || ''}`.trim() ||
    fallbackName;

  if (!mailing && !postal && !profileRes.address_line_1) return null;

  return {
    full_name: fullName,
    street_address: mailing?.line1 || profileRes.address_line_1 || profileRes.street || '',
    address_line_1: mailing?.line1 || profileRes.address_line_1 || profileRes.street || '',
    address_line_2: mailing?.line2 || profileRes.address_line_2 || '',
    city: mailing?.city || profileRes.city || '',
    state: mailing?.state || profileRes.state || '',
    zip: postal,
    postal_code: postal,
    source: 'user_profile',
  };
}

/**
 * Hydrate a selectable address option or sender record with both zip and postal_code set.
 */
export function hydrateAddressRecord(raw = {}, defaults = {}) {
  const postal =
    extractPostalCode(raw) ||
    extractPostalCode(defaults) ||
    '';

  const address_line_1 = (
    raw.address_line_1 ||
    raw.street_address ||
    raw.street ||
    raw.line1 ||
    defaults.address_line_1 ||
    ''
  )
    .toString()
    .toUpperCase()
    .trim();

  const address_line_2 = (
    raw.address_line_2 ||
    raw.unit ||
    raw.apt ||
    raw.line2 ||
    defaults.address_line_2 ||
    ''
  )
    .toString()
    .toUpperCase()
    .trim();

  const city = (raw.city || defaults.city || '').toString().toUpperCase().trim();
  const state = (raw.state || defaults.state || '').toString().toUpperCase().slice(0, 2).trim();

  const full_name = (
    raw.full_name ||
    raw.name ||
    defaults.full_name ||
    ''
  )
    .toString()
    .toUpperCase()
    .trim();

  return {
    ...raw,
    full_name,
    address_line_1,
    address_line_2,
    city,
    state,
    zip: postal,
    postal_code: postal,
  };
}

/** Sender address shape for envelope state (Step 7–8). */
export function toSenderAddress(raw, defaults = {}) {
  const hydrated = hydrateAddressRecord(raw, defaults);
  return {
    full_name: hydrated.full_name,
    address_line_1: hydrated.address_line_1,
    address_line_2: hydrated.address_line_2,
    city: hydrated.city,
    state: hydrated.state,
    postal_code: hydrated.postal_code,
    zip: hydrated.zip,
    source: raw.source || defaults.source || hydrated.source || 'manual',
    selected_address_id: raw.selected_address_id || raw.id || defaults.selected_address_id,
  };
}

/** Display: "BROOKLYN, NY 11236" */
export function formatCityStateZip(addr) {
  if (!addr) return '';
  const city = (addr.city || '').trim();
  const state = (addr.state || '').trim();
  const zip = getResolvedPostalCode(addr);
  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(zip ? ' ' : '');
}

/** Dev-only pipeline logging (no production noise). */
export function logAddressPipeline(stage, payload) {
  if (process.env.NODE_ENV !== 'development') return;
  console.info(`[STEP 7 ADDRESS] ${stage}`, payload);
}
